import { FC, useContext, useEffect, useRef, useState } from "react";
import { useAuthContext } from "../../context/authContext";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../context/notificationContext";
import { useDebounce } from "../../hooks/useDebounce";
import { useFilterSearch } from "../../hooks/useFilterSearch";
import useInitializer from "../../hooks/useInitializer";
import useListFilters from "../../hooks/useListFilters";
import useI18n from "../../hooks/useTranslations";
import {
  ConfigurationSharingAccess,
  InsertConfigurationSharingAccessInviteBody,
} from "../../http/types/configurationSharingAccesses";
import { useConfigurationSharingAccessesApi } from "../../http/useConfigurationSharingAccesses";
import { PaginationQuery } from "../../types";
import { emailRegex, formatTimeStamp } from "../../utils";
import SvgAdd from "../icons/Add";
import SvgCancel from "../icons/Cancel";
import SvgDelete from "../icons/Delete";
import SvgRedo from "../icons/Redo";
import Button from "../ui/Button";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import Input from "../ui/Input";
import ModalDialog from "../ui/ModalDialog";
import Switch from "../ui/Switch";
import Table from "../ui/Table";
import TableHeading from "../ui/TableHeading";
import styles from "./ConfigurationSharingAccessesContent.module.css";

interface ConfigurationSharingAccessesContentProps {
  configurationId: string;
  isAdmin?: boolean;
  onReady?: () => void;
}

interface InviteForm {
  userEmail: string | null;
  edit: boolean;
}

const ConfigurationSharingAccessesContent: FC<ConfigurationSharingAccessesContentProps> = ({
  configurationId,
  isAdmin,
  onReady,
}) => {
  const i18n = useI18n();
  const auth = useAuthContext();
  const configurationSharingAccessesApi = useConfigurationSharingAccessesApi();

  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters();
  const aborter = useRef(new AbortController());
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "configurationSharingAccesses");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInviteModalOpen, setInviteModalOpen] = useState(false);
  const [accessToCancel, setAccessToCancel] = useState<ConfigurationSharingAccess>();
  const [accessToDelete, setAccessToDelete] = useState<ConfigurationSharingAccess>();

  const debouncedValue = useDebounce(searchTerm);
  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { data, error } = configurationSharingAccessesApi.useSearchConfigurationSharingAccesses(
    configurationId,
    query,
    aborter.current.signal,
    isAdmin,
  );

  const isInitialized = useInitializer(data || error);

  useEffect(() => {
    isInitialized && onReady?.();
  }, [isInitialized]);

  const isPending = (item: ConfigurationSharingAccess) =>
    !item.user && !item.acceptedAt && !item.rejectedAt && !item.cancelled;

  const isActive = (item: ConfigurationSharingAccess) =>
    !!item.user && !!item.acceptedAt && !item.rejectedAt && !item.cancelled;

  const getStatus = (item: ConfigurationSharingAccess) => {
    if (isActive(item)) {
      return i18n.translation.projects.invitationStatus.accepted;
    }
    if (item.cancelled) {
      return i18n.translation.projects.invitationStatus.cancelled;
    }
    if (item.rejectedAt) {
      return i18n.translation.projects.invitationStatus.rejected;
    }
    return i18n.translation.projects.invitationStatus.pending;
  };

  const formatDate = (value?: string) => (value ? formatTimeStamp(value, auth.identity?.language) : "");

  const getStatusDate = (item: ConfigurationSharingAccess) => {
    if (isActive(item)) {
      return item.acceptedAt;
    }
    if (item.rejectedAt) {
      return item.rejectedAt;
    }
    return item.sentAt;
  };

  const onInviteSubmit = () => async (formData: InviteForm) => {
    setIsSubmitting(true);
    const body: InsertConfigurationSharingAccessInviteBody = {
      email: formData.userEmail ?? "",
      configurationId,
      language: auth.identity?.language ?? "",
      edit: formData.edit,
    };

    try {
      await configurationSharingAccessesApi.insertConfigurationSharingAccessInvite(configurationId, body, isAdmin);
      setInviteModalOpen(false);
      dispatch(showSuccessNotification(i18n.translation, i18n.translation.common.invitationHasBeenSentSuccessfully));
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    } finally {
      setIsSubmitting(false);
    }
  };

  const onToggleEdit = (item: ConfigurationSharingAccess, edit: boolean) => {
    setIsSubmitting(true);
    configurationSharingAccessesApi
      .updateConfigurationSharingAccess(configurationId, item.id, { edit }, isAdmin)
      .then(() => dispatch(showSuccessNotification(i18n.translation)))
      .catch((err) => dispatch(showErrorNotification(i18n, err)))
      .finally(() => setIsSubmitting(false));
  };

  if (!data && !error && !isInitialized) {
    return;
  }

  return (
    <>
      <TableHeading
        className={styles.heading}
        isSearchable
        searchValue={searchTerm}
        searchPlaceholder={i18n.translation.common.search}
        searchButtons={
          <Button glyph={SvgAdd} buttonProps={{ onClick: () => setInviteModalOpen(true), disabled: !!error }}>
            {i18n.translation.users.invite}
          </Button>
        }
        onSearchChange={handleSearchChange}
      />
      <Table<ConfigurationSharingAccess>
        filters={filters}
        isTall
        head={
          <tr>
            <th align="left">{i18n.translation.users.usersEmail}</th>
            <th align="left">{i18n.translation.common.status}</th>
            <th align="left">{i18n.translation.common.date}</th>
            <th align="left">{i18n.translation.common.canEdit}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>{item.user?.email ?? item.email}</td>
            <td>{getStatus(item)}</td>
            <td>{formatDate(getStatusDate(item))}</td>
            <td onClick={(e) => e.stopPropagation()}>
              <Switch
                id={`edit-${item.id}`}
                checked={item.edit}
                onChange={(checked) => onToggleEdit(item, checked)}
                disabled={isSubmitting || !isActive(item)}
              />
            </td>
          </>
        )}
        rowActions={(item) => [
          {
            title: i18n.translation.projects.resendInvite,
            onClick: () => {
              setIsSubmitting(true);
              configurationSharingAccessesApi
                .resendConfigurationSharingAccessInvite(configurationId, item.id, isAdmin)
                .then(() =>
                  dispatch(
                    showSuccessNotification(
                      i18n.translation,
                      i18n.translation.common.invitationHasBeenSentSuccessfully,
                    ),
                  ),
                )
                .catch((err) => dispatch(showErrorNotification(i18n, err)))
                .finally(() => setIsSubmitting(false));
            },
            icon: SvgRedo,
            visible: isPending(item) || item.cancelled,
            isDisabled: isSubmitting,
          },
          {
            title: i18n.translation.common.cancel,
            onClick: () => setAccessToCancel(item),
            icon: SvgCancel,
            visible: !isAdmin && isPending(item),
            isDisabled: isSubmitting,
          },
          {
            title: isActive(item) ? i18n.translation.common.unshare : i18n.translation.common.delete,
            onClick: () => setAccessToDelete(item),
            icon: SvgDelete,
            isDivider: true,
            color: "red",
            isDisabled: isSubmitting,
          },
        ]}
        total={data?.total}
        error={!!error}
      />

      {isInviteModalOpen && (
        <ModalDialog title={i18n.translation.projects.shareWithUser} onClose={() => setInviteModalOpen(false)}>
          <Form<InviteForm>
            defaultValues={{
              userEmail: null,
              edit: false,
            }}
            submitText={i18n.translation.common.invite}
            disabled={isSubmitting}
            secondaryButton={
              <Button buttonProps={{ onClick: () => setInviteModalOpen(false) }} type="secondary">
                {i18n.translation.common.cancel}
              </Button>
            }
            onSubmit={onInviteSubmit}
          >
            {({ register, formState: { errors }, setValue, watch }) => {
              const edit = watch("edit");

              return (
                <FormFieldsContainer fullWidth>
                  <FormField label={i18n.translation.users.usersEmail} error={errors.userEmail}>
                    {({ labelId, isOptional, isInvalid }) => (
                      <Input
                        id={labelId}
                        isInvalid={isInvalid}
                        autoComplete="on"
                        {...register("userEmail", {
                          required: !isOptional,
                          pattern: {
                            value: emailRegex,
                            message: i18n.translation.common.fieldInvalidValue,
                          },
                          minLength: 1,
                        })}
                      />
                    )}
                  </FormField>
                  <FormField label={i18n.translation.projects.allowUserToEditSharedConfiguration} isOptional>
                    {({ labelId }) => (
                      <Switch id={labelId} checked={edit} onChange={(checked) => setValue("edit", checked)} />
                    )}
                  </FormField>
                </FormFieldsContainer>
              );
            }}
          </Form>
        </ModalDialog>
      )}

      {!!accessToCancel && (
        <ModalDialog
          onClose={() => setAccessToCancel(undefined)}
          title={i18n.translation.common.cancel}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setAccessToCancel(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                configurationSharingAccessesApi
                  .cancelConfigurationSharingAccessInvite(configurationId, accessToCancel.id)
                  .then(() => {
                    setAccessToCancel(undefined);
                    dispatch(showSuccessNotification(i18n.translation));
                  })
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.projects.questions.cancelInvite}
        </ModalDialog>
      )}

      {!!accessToDelete && (
        <ModalDialog
          onClose={() => setAccessToDelete(undefined)}
          title={isActive(accessToDelete) ? i18n.translation.users.remove : i18n.translation.common.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setAccessToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                if (!accessToDelete.id) {
                  return;
                }

                setIsSubmitting(true);
                configurationSharingAccessesApi
                  .deleteConfigurationSharingAccess(configurationId, accessToDelete.id, isAdmin)
                  .then(() => {
                    setAccessToDelete(undefined);
                    dispatch(showSuccessNotification(i18n.translation));
                  })
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.projects.questions.unshare}
        </ModalDialog>
      )}
    </>
  );
};

export default ConfigurationSharingAccessesContent;
